import { forwardRef, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import NumberFormat from 'react-number-format';

import InfoIcon from '@mui/icons-material/Info';

import { ReactComponent as ActegonIcon } from './actegon.svg';

import './App.css';

const calculateIPAKATA = (income, expense) => {
  const teteles = 25;
  const egyszerusitett = income < 8000 ? income * 0.8 * 0.01 : Infinity;
  const koltsegelszamoals = (income - expense) * 0.01;

  return Math.min(teteles, egyszerusitett, koltsegelszamoals);
};

const calculateIPAAtalany = (income, koltseghanyad) => {
  return income * (1 - koltseghanyad) * 1.2 * 0.01;
};

const calculateIPATeteles = (income, expense) => {
  const egyszerusitett = income < 8000 ? income * 0.8 * 0.01 : Infinity;
  const koltsegelszamoals = (income - expense) * 0.01;

  return Math.min(egyszerusitett, koltsegelszamoals);
};

const calculateSZJA = (adoalap, szjaMentes) =>
  Math.max((adoalap - (szjaMentes ? 656.785 * 12 : 0)) * 0.15, 0);
const calculateTB = (adoalap, minimalber, monthly, foallasu, nyugdijas) => {
  if (nyugdijas) return 0;
  return foallasu
    ? monthly
      ? Math.max(adoalap * 0.185, minimalber * 0.185)
      : Math.max(adoalap * 0.185, minimalber * 12 * 0.185)
    : adoalap * 0.185;
};
const calculateSZOCHO = (
  adoalap,
  minimalber,
  monthly,
  foallasu,
  nyugdijas,
  jarulek = true
) => {
  if (nyugdijas) return 0;
  return foallasu
    ? monthly
      ? Math.max(adoalap * 0.13, jarulek ? minimalber * 0.13 : 0)
      : Math.max(adoalap * 0.13, jarulek ? minimalber * 12 * 0.13 : 0)
    : adoalap * 0.13
};

function App() {
  const [income, setIncome] = useState(6000);
  const [expense, setExpense] = useState(0);
  const [koltseghanyad, setKoltseghanyad] = useState(0.45);

  const _minimalber = 322.8;
  const [minimalber, setMinimalber] = useState(_minimalber);
  const [allastipus, setAllastipus] = useState('foallasu');
  const [monthly, setMonthly] = useState(false);
  const [pontosOsszeg, setPontosOsszeg] = useState(false);
  const [szjaMentes, setSzjaMentes] = useState(false);

  const foallasu = allastipus === 'foallasu';
  const nyugdijas = allastipus === 'nyugdijas';

  const incomeInThousands = pontosOsszeg ? income / 1000 : income;
  const expenseInThousands = pontosOsszeg ? expense / 1000 : expense;

  let ipaKATA = monthly
    ? calculateIPAKATA(incomeInThousands * 12, expenseInThousands)
    : calculateIPAKATA(incomeInThousands, expenseInThousands);
  let ipaAtalany = monthly
    ? calculateIPAAtalany(incomeInThousands * 12, koltseghanyad)
    : calculateIPAAtalany(incomeInThousands, koltseghanyad);
  let ipaTeteles = monthly
    ? calculateIPATeteles(incomeInThousands * 12, expenseInThousands) / 12
    : calculateIPATeteles(incomeInThousands, expenseInThousands);

  let kata = (foallasu ? 50 : 25) * 12;
  let overIncomeKATA = monthly
    ? Math.max(0, (incomeInThousands * 12 - 18_000) * 0.4)
    : Math.max(0, (incomeInThousands - 18_000) * 0.4);

  const adomentesAlap = _minimalber * 6;

  let mentesitett = monthly
    ? Math.min(adomentesAlap, incomeInThousands * 12 * (1 - koltseghanyad))
    : Math.min(adomentesAlap, incomeInThousands * (1 - koltseghanyad));
  const adoalapAtalany = monthly
    ? Math.max(incomeInThousands * (1 - koltseghanyad) - mentesitett / 12, 0)
    : Math.max(incomeInThousands * (1 - koltseghanyad) - mentesitett, 0);
  let szjaAtalany = monthly
    ? calculateSZJA(adoalapAtalany * 12, szjaMentes)
    : calculateSZJA(adoalapAtalany, szjaMentes);
  let tbAtalany = calculateTB(
    adoalapAtalany,
    minimalber,
    monthly,
    foallasu,
    nyugdijas
  );

  let szochoAtalany = calculateSZOCHO(
    adoalapAtalany,
    minimalber,
    monthly,
    foallasu,
    nyugdijas
  );

  // const szjaTeteles =
  // nincs vállalkozói kivét
  const tbTetelesJarulek = calculateTB(
    0,
    minimalber,
    monthly,
    foallasu,
    nyugdijas
  );
  const szochoTetelesJarulek = calculateSZOCHO(
    0,
    minimalber,
    monthly,
    foallasu,
    nyugdijas
  );
  // osztalékalap
  const adoalapTeteles =
    incomeInThousands -
    expenseInThousands -
    tbTetelesJarulek -
    szochoTetelesJarulek -
    ipaTeteles;
  const vszja = adoalapTeteles * 0.09;
  const szjaTeteles = monthly
    ? calculateSZJA((adoalapTeteles - vszja) * 12, szjaMentes) / 12
    : calculateSZJA(adoalapTeteles - vszja, szjaMentes);
  const szochoTetelesOsztalek = calculateSZOCHO(
    adoalapTeteles - vszja,
    minimalber,
    monthly,
    foallasu,
    nyugdijas,
    false
  );

  if (monthly) {
    overIncomeKATA /= 12;
    ipaKATA /= 12;
    ipaAtalany /= 12;
    kata /= 12;
    mentesitett /= 12;
    szjaAtalany /= 12;
  }

  const totalKATA = kata + overIncomeKATA + ipaKATA;
  const totalAtalany = szjaAtalany + tbAtalany + szochoAtalany + ipaAtalany;

  const totalTeteles =
    tbTetelesJarulek +
    vszja +
    szjaTeteles +
    szochoTetelesJarulek + szochoTetelesOsztalek +
    ipaTeteles;

  const NumberFormatCustom = useMemo(
    () =>
      forwardRef(function NumberFormatCustom(props, ref) {
        const { onChange, ...other } = props;

        return (
          <NumberFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values) => {
              onChange({
                target: {
                  name: props.name,
                  value: values.value,
                },
              });
            }}
            thousandSeparator=' '
            decimalSeparator=','
            isNumericString
            suffix={pontosOsszeg ? ' Ft' : ' eFt'}
          />
        );
      }),
    [pontosOsszeg]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: '100%',
        marginTop: { xs: 0, sm: 0, md: 2, lg: 2, xl: 2 },
        marginBottom: { xs: 0, sm: 0, md: 2, lg: 2, xl: 2 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 3,
          width: 700,
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='h4'>KATA vs Átalányadó kalkulátor 2026</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={monthly}
                onChange={(e) => {
                  setMonthly(e.target.checked);
                  setIncome(
                    e.target.checked ? Math.floor(income / 12) : income * 12
                  );
                  setExpense(
                    e.target.checked ? Math.floor(expense / 12) : expense * 12
                  );
                }}
              />
            }
            label='Havi osztás'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={pontosOsszeg}
                onChange={(e) => {
                  setPontosOsszeg(e.target.checked);
                  setIncome(
                    e.target.checked ? income * 1000 : Math.floor(income / 1000)
                  );
                  setExpense(
                    e.target.checked
                      ? expense * 1000
                      : Math.floor(expense / 1000)
                  );
                }}
              />
            }
            label='Pontos összeg beírása ezer Ft helyett'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={szjaMentes}
                onChange={(e) => setSzjaMentes(e.target.checked)}
              />
            }
            label='25 év alatti'
          />
          <FormControl>
            <FormHelperText>
              {!monthly
                ? `Mennyi az éves bevételed${pontosOsszeg ? '' : ' (eFt)'}?`
                : `Mennyi a havi bevételed${pontosOsszeg ? '' : ' (eFt)'}?`}
            </FormHelperText>
            <TextField
              onChange={(e) =>
                setIncome(
                  Math.min(
                    Math.max(e.target.value, 0),
                    (koltseghanyad === 0.9 ? _minimalber * 600 : _minimalber * 120) *
                      (pontosOsszeg ? 1000 : 1)
                  ) || 0
                )
              }
              value={income}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              InputProps={{
                inputComponent: NumberFormatCustom,
                endAdornment: (
                  <InputAdornment position='end'>
                    <Tooltip
                      title={`KATA esetén évi ${12000000..toLocaleString('hu')} Ft felett büntetőadó. Átalányadó esetén maximum ${ (_minimalber * 120 * 1000).toLocaleString('hu')} Ft (90%-os költséghányad esetén ${(_minimalber * 600 * 1000).toLocaleString('hu')} Ft).`}
                    >
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <FormControl>
            <FormHelperText>
              {!monthly
                ? `Mennyi az éves költséged${pontosOsszeg ? '' : ' (eFt)'}?`
                : `Mennyi a havi költséged${pontosOsszeg ? '' : ' (eFt)'}?`}
            </FormHelperText>
            <TextField
              onChange={(e) =>
                setExpense(
                  Math.min(
                    Math.max(e.target.value, 0),
                    (koltseghanyad === 0.9 ? 120000 : 24000) *
                      (pontosOsszeg ? 1000 : 1)
                  ) || 0
                )
              }
              value={expense}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              InputProps={{
                inputComponent: NumberFormatCustom,
                // endAdornment: (
                //   <InputAdornment position='end'>
                //     <Tooltip
                //       title={`KATA esetén 12.000.000 Ft felett büntetőadó. Átalányadó esetén maximum 24.000.000 Ft.`}
                //     >
                //       <IconButton>
                //         <InfoIcon />
                //       </IconButton>
                //     </Tooltip>
                //   </InputAdornment>
                // ),
              }}
            />
          </FormControl>
          <FormControl>
            <FormHelperText>
              Főállású, méllékállású vagy nyugdíjas egyéni vállalkozó vagy?
            </FormHelperText>
            <Select
              value={allastipus}
              className='andornment-select'
              onChange={(e) => setAllastipus(e.target.value)}
              endAdornment={
                <InputAdornment position='end' style={{}}>
                  <Tooltip
                    title={`Mellékállású egyéni vállalkozó: rendelkezik legalább 36 órás munkaviszonnyal VAGY nappali tagozaton tanul VAGY saját jogú nyugdíjas (kiegészítő tevékenységet folytat)`}
                  >
                    <IconButton>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            >
              <MenuItem value={'foallasu'}>Főállású</MenuItem>
              <MenuItem value={'mellekallasu'}>Mellékállású</MenuItem>
              <MenuItem value={'nyugdijas'}>Nyugdíjas</MenuItem>
            </Select>
          </FormControl>
          {foallasu ? (
            <FormControl>
              <FormHelperText>
                A tevékenység középiskolai végzettséghez, illetőleg
                szakképesítéshez van kötve?
              </FormHelperText>
              <Select
                value={minimalber}
                onChange={(e) => setMinimalber(e.target.value)}
                className='andornment-select'
                endAdornment={
                  <InputAdornment position='end' style={{}}>
                    <Tooltip
                      title={`Amennyiben főállású egyéni vállalkozóról van szó, akkor bevételtől függetlenül minden hónapban be kell fizetni a járulékokat (TB, SZOCHO) a minimálbér értéke után. Amennyiben tevékenység alapján emelt minimálbér (bérminimum 326 ezer Ft) járna az alap minimálbér (266.8 ezer Ft) helyett, akkor a kalkulátor ez alapján számol.`}
                    >
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                }
              >
                <MenuItem value={348.8}>Igen</MenuItem>
                <MenuItem value={_minimalber}>Nem</MenuItem>
              </Select>
            </FormControl>
          ) : null}
          <FormControl>
            <FormHelperText>
              Milyen költséghányadú tevékenységet végzel?
            </FormHelperText>
            <Select
              value={koltseghanyad}
              onChange={(e) => setKoltseghanyad(e.target.value)}
              className='andornment-select'
              endAdornment={
                <InputAdornment position='end' style={{}}>
                  <Tooltip
                    title={
                      <div>
                        <b>45%-os költséghányad</b>: Ez az alapeset, ezt nem
                        sorolja fel a törvény, ha nem férsz bele a 80%-os vagy a
                        90%-os költséghányadba, akkor a 45%-os költséghányad alá
                        tartozol.<br></br>
                        <b>80%-os költséghányad</b>: a) mezőgazdasági,
                        erdőgazdálkodási (TESZOR 01, 02), bányászati (TESZOR
                        05-től 09-ig) és feldolgozóipari (TESZOR 10-től 32-ig)
                        termék-előállítás, építőipari kivitelezés (TESZOR 41,
                        42); b) mezőgazdasági, betakarítást követő szolgáltatás
                        (TESZOR 01.6), vadgazdálkodáshoz kapcsolódó szolgáltatás
                        (TESZOR 01.70.10), erdészeti szolgáltatás (TESZOR
                        02.40.10) és zöldterület-kezelés (TESZOR 81.30.10); c)
                        halászati szolgáltatás (TESZOR 03.00.71),
                        halgazdálkodási szolgáltatás (TESZOR 03.00.72); d)
                        feldolgozóipari szolgáltatás (TESZOR 10-től 32-ig) a
                        bérmunkában végzett szolgáltatás és az egyéb
                        sokszorosítás (TESZOR 18.20) kivételével; e) építőipari
                        szolgáltatás (TESZOR 43); f) ipari gép, berendezés,
                        eszköz javítása (TESZOR 33.1), gépjárműjavítás (TESZOR
                        45.20), személyi, háztartási cikk javítása (TESZOR
                        95.2), épületgépészeti berendezések javítása (TESZOR
                        43.21, 43.22, 43.29); g) a taxis személyszállítás
                        (TESZOR 49.32.11) személygépjármű kölcsönzése vezetővel
                        (TESZOR 49.32.12), egyéb máshová nem sorolt szárazföldi
                        személyszállítás (TESZOR 49.39.39), közúti áruszállítás
                        (TESZOR 49.41.1); h) számítógép, kommunikációs eszköz
                        javítása (TESZOR 95.1); i) fényképészet (TESZOR 74.20);
                        j) textil, szőrme mosása, tisztítása (TESZOR 96.01),
                        fodrászat, szépségápolás (TESZOR 96.02),
                        hobbiállat-gondozás (TESZOR 96.09.11); k) a kereskedelmi
                        tevékenységek végzésének feltételeiről szóló
                        kormányrendelet alapján folytatott vendéglátó
                        tevékenység (TESZOR 56). és kiskereskedelmi
                        tevékenységből bevételt szerző egyéni vállalkozó. Ez azt
                        jelenti, hogy ha nem kizárólag kiskereskedelmi
                        tevékenységet végez, hanem mellette van másik
                        kedvezményes költséghányadú végzett tevékenység is,
                        akkor 80%-os költséghányadot lehet csak alkalmazni.
                        <br></br>
                        <b>90%-os költséghányad</b>: aki az adóév egészében
                        kizárólag kiskereskedelmi tevékenységből szerez bevételt
                        (azaz más tevékenységből nem lehet bevétele)
                        mezőgazdasági őstermelő.
                      </div>
                    }
                  >
                    <IconButton>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            >
              <MenuItem value={0.45}>45%</MenuItem>
              <MenuItem value={0.8}>80%</MenuItem>
              <MenuItem value={0.9}>90%</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TableContainer sx={{ maxWidth: '100%' }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell width={80} align='right'>
                  Tétel
                </TableCell>
                <TableCell width={100} align='right'>
                  KATA
                </TableCell>
                <TableCell width={100} align='right'>
                  Átalányadózás
                </TableCell>
                <TableCell width={100} align='right'>
                  Tételes költségelszámolás
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align='right'>Bevétel</TableCell>
                <TableCell align='right'>
                  {(incomeInThousands * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell align='right'>
                  {(incomeInThousands * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell align='right'>
                  {(incomeInThousands * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>KATA</TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {(kata * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell style={{ color: 'red' }} align='right'></TableCell>
                <TableCell style={{ color: 'red' }} align='right'></TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>Plusz fizetendő KATA</TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {(overIncomeKATA * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell style={{ color: 'red' }} align='right'></TableCell>
                <TableCell style={{ color: 'red' }} align='right'></TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>IPA (1%)</TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {Math.floor(ipaKATA * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {Math.floor(ipaAtalany * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {Math.floor(ipaTeteles * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  Költséghányad ({koltseghanyad * 100}%)
                </TableCell>
                <TableCell align='right'></TableCell>
                <TableCell align='right'>
                  {`${(incomeInThousands * koltseghanyad * 1000).toLocaleString(
                    'hu-HU'
                  )} Ft`}
                </TableCell>
                <TableCell align='right'></TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>Jövedelem</TableCell>
                <TableCell align='right'></TableCell>
                <TableCell align='right'>
                  {(
                    incomeInThousands *
                    (1 - koltseghanyad) *
                    1000
                  ).toLocaleString('hu-HU')}{' '}
                  Ft
                </TableCell>
                <TableCell align='right' style={{ fontWeight: 'bold' }}>
                  {(
                    (incomeInThousands - expenseInThousands) *
                    1000
                  ).toLocaleString('hu-HU')}{' '}
                  Ft
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>Mentesített adóalap</TableCell>
                <TableCell align='right'></TableCell>
                <TableCell align='right'>
                  {`${(mentesitett * 1000).toLocaleString('hu-HU')} Ft`}
                </TableCell>
                <TableCell align='right'></TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>Korrigált adóalap</TableCell>
                <TableCell align='right'></TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align='right'>
                  {(adoalapAtalany * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell align='right'></TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>VSZJA (9%)</TableCell>
                <TableCell style={{ color: 'red' }} align='right'></TableCell>
                <TableCell style={{ color: 'red' }} align='right'></TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {Math.floor(vszja * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>SZJA (15%)</TableCell>
                <TableCell style={{ color: 'red' }} align='right'></TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {(szjaAtalany * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {Math.floor(szjaTeteles * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>TB járulék (18,5%)</TableCell>
                <TableCell style={{ color: 'red' }} align='right'></TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {(tbAtalany * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {Math.floor(tbTetelesJarulek * 1000).toLocaleString('hu-HU')}{' '}
                  Ft
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>SZOCHO (13%)</TableCell>
                <TableCell style={{ color: 'red' }} align='right'></TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {Math.floor(szochoAtalany * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {Math.floor(
                    Math.min(
                      szochoTetelesJarulek + szochoTetelesOsztalek,
                      monthly ? (4800 / 12) * 0.13 : 4800 * 0.13
                    ) * 1000
                  ).toLocaleString('hu-HU')}{' '}
                  Ft
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>Valós költség</TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {(expenseInThousands * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {(expenseInThousands * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
                <TableCell style={{ color: 'red' }} align='right'>
                  {(expenseInThousands * 1000).toLocaleString('hu-HU')} Ft
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>Összes adó</TableCell>
                <TableCell align='right'>
                  {Math.floor(totalKATA * 1000).toLocaleString('hu-HU')} Ft (
                  {((totalKATA / incomeInThousands) * 100).toFixed(2)}%)
                </TableCell>
                <TableCell align='right'>
                  {Math.floor(totalAtalany * 1000).toLocaleString('hu-HU')} Ft (
                  {((totalAtalany / incomeInThousands) * 100).toFixed(2)}%)
                </TableCell>
                <TableCell align='right'>
                  {Math.floor(totalTeteles * 1000).toLocaleString('hu-HU')} Ft (
                  {((totalTeteles / incomeInThousands) * 100).toFixed(2)}%)
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }} align='right'>
                  Profit
                </TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align='right'>
                  {`${Math.floor(
                    (incomeInThousands - expenseInThousands - totalKATA) * 1000
                  ).toLocaleString('hu-HU')} Ft`}
                </TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align='right'>
                  {`${Math.floor(
                    (incomeInThousands - expenseInThousands - totalAtalany) *
                      1000
                  ).toLocaleString('hu-HU')} Ft`}
                </TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align='right'>
                  {`${Math.floor(
                    (incomeInThousands - expenseInThousands - totalTeteles) *
                      1000
                  ).toLocaleString('hu-HU')} Ft`}
                </TableCell>
              </TableRow>
              {/* <TableRow
            key={row.name}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCell component="th" scope="row">
              {row.name}
            </TableCell>
            <TableCell align="right">{row.calories}</TableCell>
            <TableCell align="right">{row.fat}</TableCell>
            <TableCell align="right">{row.carbs}</TableCell>
            <TableCell align="right">{row.protein}</TableCell>
          </TableRow> */}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ marginTop: 1 }}>
          <a
            href='https://actegon.com'
            target='_blank'
            rel='noreferrer'
            style={{
              display: 'block',
              textAlign: 'center',
              marginTop: 15,
              marginBottom: 10,
            }}
          >
            <ActegonIcon style={{ width: 180, height: 'auto' }} />
          </a>
          <Typography variant='body1' gutterBottom>
            Kalkuláld ki, hogy KATA adózásról átalányadózásra vagy tételes
            költségelszámolásra váltva milyen opcióid vannak.
          </Typography>
          <Typography variant='body1' gutterBottom>
            Cégünk szoftverfejlesztéssel foglalkozik. Dinamikusan dolgozunk.
            Amennyiben úgy gondolod, hogy tudnánk együtt dolgozni, dobj egy
            e-mailt a <a href='mailto:hello@actegon.com'>hello@actegon.com</a>{' '}
            címre.
          </Typography>
          <Typography variant='body2' style={{ fontStyle: 'italic' }}>
            A kalkulátor megjelenített értékei tájékoztató jellegűek. Azokért
            felelősséget nem vállalunk.
          </Typography>
          <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
          <Typography variant='overline'>
            Copyright © {new Date().getFullYear()} | Actegon Solutions Kft. All
            rights reserved. |{' '}
            <a
              href='https://rezsi.actegon.com'
              rel='noreferrer'
              target='_blank'
            >
              Rezsi kalkulátor
            </a>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default App;
